
import {
  Vue,
  Component,
  Prop,
  Watch,
} from 'vue-property-decorator';
import { IAttachedFile } from '@/types/attachedFile';
import { uploadFile } from '@/api/attachedFile';

@Component
export default class PrImageUploads extends Vue {
  // ⭐️ [수정 1] Prop 이름을 .sync에 맞게 변경합니다. (initial... -> ...)
  @Prop({ type: Array, default: () => [] })
  readonly files!: IAttachedFile[];

  @Prop({ type: Array, default: () => [] })
  readonly fileUids!: string[];

  @Prop({ type: Array, default: () => [0] })
  readonly inputs!: number[];

  // Local State: 컴포넌트 내부에서 상태를 관리합니다. (기존과 동일)
  private localFiles: IAttachedFile[] = [];

  private localFileUids: string[] = [];

  private localInputs: number[] = [0]; // 기본값 [0] 유지

  // ⭐️ [수정 2] Prop 데이터를 Local State로 복사하는 헬퍼 메서드 추가
  private syncPropsToLocal() {
    // 부모가 보낸 props를 로컬 상태로 복사합니다.
    this.localFiles = [...this.files];
    this.localFileUids = [...this.fileUids];
    this.localInputs = [...this.inputs];

    // 방어 코드:
    // 만약 파일이나 UID는 있는데 input 배열이 비어있거나 기본값 [0]이면,
    // 데이터 기준으로 input 배열을 재구성합니다. (Company.vue가 이미 계산하지만 만약을 대비)
    const hasFiles = this.localFiles.length > 0 || this.localFileUids.length > 0;
    const hasInputs = this.localInputs.length > 1 || (this.localInputs.length === 1 && this.localInputs[0] !== 0);

    if (hasFiles && !hasInputs) {
      // 파일이나 UID 목록 중 더 긴 것을 기준으로 input 인덱스 배열 생성
      const count = Math.max(this.localFiles.length, this.localFileUids.length);
      this.localInputs = Array.from({ length: count }, (_, i) => i);
    }

    // 만약 모든 동기화 후에도 input이 없다면 (예: API 응답이 [] 였던 경우)
    // 기본 입력 필드 1개를 보장합니다.
    if (this.localInputs.length === 0) {
      this.localInputs = [0];
    }

    // 동기화 후, 부모에게 한번 알려줍니다. (선택적)
    // this.updateParent();
  }

  // ⭐️ [수정 3] created 훅에서 1차 동기화 시도
  created() {
    this.syncPropsToLocal();
  }

  // ⭐️ [수정 4] 'inputs' Prop을 Watch 하여 비동기 데이터 도착에 대응
  @Watch('inputs')
  onInputsPropChanged(newInputs: number[], oldInputs: number[]) {
    // 로컬 상태가 아직 기본값([0])이고, 부모로부터 새로운 데이터(기본값이 아닌)가 도착했을 때 동기화
    const localIsDefault = this.localInputs.length === 1 && this.localInputs[0] === 0;
    const newIsData = newInputs.length > 0; // 부모가 빈 배열 []을 보낼 수도 있음
    if (localIsDefault && newIsData) {
      this.syncPropsToLocal();
    }
  }

  mounted() {
    // console.log('mounted');
  }

  // Helper: 부모 컴포넌트에 변경된 데이터를 알립니다. (기존과 동일)
  private updateParent() {
    this.$emit('update:files', this.localFiles);
    this.$emit('update:fileUids', this.localFileUids);
    this.$emit('update:inputs', this.localInputs);
  }

  // 파일 변경 핸들러 (기존과 동일)
  private handleChangeFile(e: any, idx: number) {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024 * 10) {
      alert('최대 10MB 까지 업로드 가능합니다.');
      return;
    }
    if (!file.type.startsWith('image')) {
      alert('이미지 형식만 업로드 가능합니다.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    uploadFile('client/company', formData).then((res) => {
      // Vue.set을 사용하여 반응성 보장
      this.$set(this.localFiles, idx, res.data);
      this.$set(this.localFileUids, idx, res.data.uid);
      this.updateParent();
    });
  }

  // 입력 필드 추가 (기존과 동일)
  private addPrImgInput() {
    this.localInputs.push(this.localInputs.length);
    // 중요: 파일과 UID 배열에도 빈 값을 넣어 길이를 맞춰야 moveItem 시 인덱스가 밀리지 않습니다.
    this.localFiles.push(null as any);
    this.localFileUids.push('');
    this.updateParent();
  }

  // 이미지 제거 (기존과 동일)
  // [개선] pop() 대신 splice()를 사용하여 정확한 인덱스의 input을 제거합니다.
  private removePrImg(index: number) {
    this.localInputs.splice(index, 1);
    this.localFiles.splice(index, 1);
    this.localFileUids.splice(index, 1);

    // 다 지웠을 경우 기본 1개 생성
    if (this.localInputs.length === 0) {
      this.localInputs.push(0);
      this.localFiles.push(null as any);
      this.localFileUids.push('');
    }

    this.updateParent();
  }

  // 배열 항목 이동 공용 헬퍼 함수 (기존과 동일)
  private moveItem(array: any[], fromIndex: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= array.length) return;
    const item = array.splice(fromIndex, 1)[0];
    array.splice(toIndex, 0, item);
  }

  // 위로 이동 (기존과 동일)
  private movePrImgUp(index: number) {
    if (index === 0) return;
    this.moveItem(this.localInputs, index, index - 1);
    this.moveItem(this.localFiles, index, index - 1);
    this.moveItem(this.localFileUids, index, index - 1);
    this.updateParent();
  }

  // 아래로 이동 (기존과 동일)
  private movePrImgDown(index: number) {
    if (index === this.localInputs.length - 1) return;
    this.moveItem(this.localInputs, index, index + 1);
    this.moveItem(this.localFiles, index, index + 1);
    this.moveItem(this.localFileUids, index, index + 1);
    this.updateParent();
  }
}
